import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'tfi_journal_entries_v1';

function readEntries() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function writeEntries(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage may be full or disabled — ignore
  }
}

export function useJournal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setEntries(readEntries());
    setLoading(false);
  }, []);

  const addEntry = useCallback(({ content, mood }) => {
    const entry = {
      id:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now()),
      content,
      mood: mood || null,
      created_at: new Date().toISOString(),
    };
    setEntries((prev) => {
      const next = [entry, ...prev];
      writeEntries(next);
      return next;
    });
    return entry;
  }, []);

  const deleteEntry = useCallback((id) => {
    setEntries((prev) => {
      const next = prev.filter((e) => e.id !== id);
      writeEntries(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    writeEntries([]);
    setEntries([]);
  }, []);

  /** Replace the current journal with the provided list (used by Restore). */
  const replaceAll = useCallback((list) => {
    if (!Array.isArray(list)) return 0;
    const cleaned = list
      .filter((e) => e && typeof e.content === 'string' && e.content.trim())
      .map((e) => ({
        id:
          (typeof e.id === 'string' && e.id) ||
          (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : String(Date.now() + Math.random())),
        content: String(e.content),
        mood: e.mood && typeof e.mood === 'string' ? e.mood : null,
        created_at:
          e.created_at && typeof e.created_at === 'string'
            ? e.created_at
            : new Date().toISOString(),
      }));
    cleaned.sort((a, b) => b.created_at.localeCompare(a.created_at));
    writeEntries(cleaned);
    setEntries(cleaned);
    return cleaned.length;
  }, []);

  return { entries, loading, addEntry, deleteEntry, clearAll, replaceAll };
}
